import React, { useEffect, useState, useRef, useCallback } from "react";
import { Bell, UserPlus, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

export function NotificationBell() {
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const prev = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotes(data.notifications);
      setUnread(data.unread);
      if (data.unread > prev.current && prev.current !== 0) {
        const latest = data.notifications.find((n) => !n.read);
        if (latest) toast(latest.message, { icon: "🔔" });
      }
      prev.current = data.unread;
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 flex items-center justify-center transition-colors">
          <Bell className="w-5 h-5 text-slate-300" />
          {unread > 0 && (
            <span data-testid="notifications-unread" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-slate-900 border-slate-800 text-slate-100 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <span className="font-semibold text-sm">Notifications</span>
          {unread > 0 && (
            <button data-testid="notifications-mark-all" onClick={markAll} className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Mark all read</button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notes.length === 0 && <div className="px-4 py-8 text-center text-slate-500 text-sm">No notifications yet</div>}
          {notes.map((n) => (
            <div key={n.id} data-testid={`notification-${n.id}`} className={`px-4 py-3 border-b border-slate-800/60 flex gap-3 ${!n.read ? "bg-blue-500/5" : ""}`}>
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0"><UserPlus className="w-4 h-4 text-blue-400" /></div>
              <div className="min-w-0">
                <p className="text-sm leading-snug">{n.message}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{new Date(n.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
